'use client'

/**
 * The feedback message textarea and its character counters.
 *
 * Lifted out of `./feedback-modal` by #7520. That file is baselined by
 * `check:file-size-ratchet` (#12440) as shrink-only, and #7520 had to touch
 * every place the length limits appear in it — so the limit-bearing markup
 * moves here rather than the baseline moving up. This is the whole of the
 * message field: the textarea, its props, and the minimum/maximum counters,
 * which is the smallest cut that keeps the limits in one component. (#7520
 * split TWO binding branches out; #13645 deleted the CRDT one, so there is
 * one textarea and it is controlled.)
 *
 * The caps themselves live in `@/lib/feedback/limits` and are shared with the
 * MCP tool and the server action. Nothing here may restate a number: the
 * counter is the only place a submitter ever sees the ceiling, so a literal
 * here is indistinguishable from the ceiling not having been raised.
 */

import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import {
  FEEDBACK_MIN_MESSAGE_LENGTH,
  FEEDBACK_MAX_MESSAGE_LENGTH,
  formatFeedbackCount,
} from '@/lib/feedback/limits'

/**
 * The textarea's props, held apart from the element so a future
 * placeholder/token change is made once (issue #11923 code review,
 * suggestion 2).
 *
 * The `data-testid` is deliberately NOT in here. It is the addressing handle
 * declared by the `ui` row of `.cadence/surfaces/7520.json` and again by
 * `.cadence/surfaces/13645.json`, and `golden-path-e2e`'s non-vacuity check
 * resolves a spec's `getByTestId('…')` against `data-testid="…"` spellings in
 * `src/` — a quoted object key (`'data-testid': '…'`) matches none of them, so
 * a testid hidden in a spread object reads to the gate as one nothing renders.
 * It sits on the element.
 *
 * Deliberately NO `maxLength`: clamping the field would truncate a pasted
 * long-form submission without telling anyone, which is the destructive
 * truncation #7520 was filed about. The cap is enforced server-side, where the
 * rejection can report the actual overage back.
 */
/**
 * The textarea's `id`, exported so `feedback-modal.tsx`'s `<label htmlFor>` is a
 * compile-time relationship rather than a second literal in a second file (Code
 * Review #1 on PR #12981, suggestion 6). Before #7520 both literals lived in one
 * file, where an inconsistent rename was at least visible in one diff.
 */
export const FEEDBACK_MESSAGE_INPUT_ID = 'feedback-message'

const FEEDBACK_MESSAGE_TEXTAREA_PROPS = {
  id: FEEDBACK_MESSAGE_INPUT_ID,
  placeholder: `Tell us what's on your mind... (minimum ${FEEDBACK_MIN_MESSAGE_LENGTH} characters)`,
  rows: 4,
  className:
    'border-stroke-regular text-text-primary focus:border-transparent resize-none',
} as const

type FeedbackMessageTextareaProps = {
  message: string
  setMessage: (value: string) => void
  onPaste: (event: React.ClipboardEvent<HTMLTextAreaElement>) => void
}

/**
 * One textarea, controlled.
 *
 * Until #13645 this component chose between two bindings on the presence of a
 * `bodyTextRef` prop: a caret-preserving UNCONTROLLED binding handed out by
 * the CRDT text adapter, and this controlled one on the localStorage
 * fallback. The CRDT stack is gone, nothing hands out that ref any more, and
 * a controlled React textarea preserves its own caret — the caret bug
 * (#11923) was a property of the CRDT projection writing over the DOM node,
 * not of controlled inputs.
 */
export function FeedbackMessageTextarea({
  message,
  setMessage,
  onPaste,
}: FeedbackMessageTextareaProps) {
  return (
    <Textarea
      {...FEEDBACK_MESSAGE_TEXTAREA_PROPS}
      data-testid="feedback-message-input"
      value={message}
      onChange={event => setMessage(event.target.value)}
      onPaste={onPaste}
    />
  )
}

type FeedbackMessageCountersProps = {
  message: string
}

/**
 * The minimum-progress counter and the length-against-cap counter.
 *
 * Both counters read the shared constants, so the maximum shown to a submitter
 * cannot drift from the maximum the server enforces (#7520).
 *
 * Both also measure the TRIMMED length, because that is what the server
 * enforces — `checkFeedbackMessageLength` trims, and so does the MCP schema.
 * The maximum counter used to read `message.length`, so a submission with
 * trailing whitespace displayed `10,050/10,000` for content the action would
 * have accepted (Code Review #1 on PR #12981, finding 3). A counter that reads
 * over-budget for an in-budget submission is the same misinformation this issue
 * removes, just at the boundary.
 *
 * The minimum indicator renders only while the minimum is UNMET (#14531). It
 * used to stay on screen as a green "10/10 minimum reached", which was noise
 * once satisfied; the met state is now communicated by the indicator going
 * away and Send becoming enabled — visually. A sighted submitter sees the
 * indicator vanish; a screen reader hears nothing from a removed node, so an
 * `sr-only` "Minimum reached" takes its place inside the live region and is
 * what gets announced. The max counter stays right-aligned.
 *
 * The region stays `aria-live="polite"` — these counters change as the
 * submitter types. #14531 also removed the `DraftStatusIndicator` that sat
 * here: its discard control read as "clear this field" but reset the whole
 * form, which is now the footer's "Start over".
 */
export function FeedbackMessageCounters({
  message,
}: FeedbackMessageCountersProps) {
  const trimmedLength = message.trim().length
  const minimumMet = trimmedLength >= FEEDBACK_MIN_MESSAGE_LENGTH

  return (
    <div
      className={cn(
        'flex items-center mt-1',
        minimumMet ? 'justify-end' : 'justify-between',
      )}
      aria-live="polite"
      aria-atomic="false"
    >
      {minimumMet ? (
        <span className="sr-only">Minimum reached</span>
      ) : (
        <span className="text-xs text-text-secondary">
          {`${trimmedLength}/${FEEDBACK_MIN_MESSAGE_LENGTH} minimum`}
        </span>
      )}
      <span className="text-xs text-text-secondary">
        {`${formatFeedbackCount(trimmedLength)}/${formatFeedbackCount(FEEDBACK_MAX_MESSAGE_LENGTH)}`}
      </span>
    </div>
  )
}
